import { get } from 'lodash';

/**
 * Collectors Service
 * Handles distributed collectors queries for infrastructure list
 */

const COLLECTORS_DISTRIBUTED_API_URL = '/ipm/v1/admin/collectors/distributed';
const COLLECTORS_BY_IP_API_URL = '/ngp/v2/inventory/collectors/which-ip';

/**
 * Fetch distributed collectors count from the server
 * @param {Object} dataService - The data service instance
 * @returns {Promise<Object>} Collectors response with total count
 */
export async function fetchDistributedCollectorsCount(dataService) {
    try {
        return await dataService.get(COLLECTORS_DISTRIBUTED_API_URL);
    } catch (error) {
        throw new Error(`Failed to fetch distributed collectors: ${error.message}`);
    }
}

/**
 * Check if the system has distributed collectors
 * @param {Object} dataService - The data service instance
 * @returns {Promise<boolean>} True if system has distributed collectors (total > 0)
 */
export async function isSystemDistributed(dataService) {
    try {
        const response = await fetchDistributedCollectorsCount(dataService);
        const total = get(response, 'total', 0);
        return total > 0;
    } catch (error) {
        throw new Error(`Failed to check if system is distributed: ${error.message}`);
    }
}

/**
 * Find the collector associated with a given IP address
 * @param {Object} dataService - The data service instance
 * @param {string} ipAddress - The IP address to check
 * @returns {Promise<Object|null>} Collector object if found, null otherwise
 */
export async function fetchCollectorByIp(dataService, ipAddress) {
    try {
        return await dataService.get(COLLECTORS_BY_IP_API_URL, { ip: ipAddress });
    } catch (error) {
        // If API returns 404 or error, it means no collector found for this IP
        return null;
    }
}

/**
 * Validate that an IP address falls under a collector's monitoring scope
 * @param {Object} dataService - The data service instance
 * @param {string} ipAddress - The IP address to validate
 * @returns {Promise<boolean>} True if IP is within a collector's scope, false otherwise
 */
export async function validateCollectorScope(dataService, ipAddress) {
    try {
        const collector = await fetchCollectorByIp(dataService, ipAddress);
        // Validation passes if collector has an _id
        return collector && get(collector, '_id') != null;
    } catch (error) {
        // On error, assume validation fails
        return false;
    }
}
